import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Collapse, InputNumber, Popconfirm, Select, Switch, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import OpcUaFieldLabel from "./OpcUaFieldLabel";
import OpcUaTextField from "./OpcUaTextField";
import NodeIdEditor, { BLANK_NUMERIC_NODE_ID } from "./NodeIdEditor";
// FLATBUFFER ENUMS
import { HistoryReadKind } from "../../../../../flatbuffers/generated/opc-ua/history-read-kind";
// INTERFACES
import type {
  OpcUaHistoryReadForm,
  OpcUaNodeIdForm,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Panel } = Collapse;
const { Option } = Select;

const HISTORY_READ_KIND_OPTIONS: { value: HistoryReadKind; label: string }[] = [
  { value: HistoryReadKind.Raw, label: "Raw" },
  { value: HistoryReadKind.Modified, label: "Modified" },
  { value: HistoryReadKind.Processed, label: "Processed" },
  { value: HistoryReadKind.AtTime, label: "AtTime" },
  { value: HistoryReadKind.Events, label: "Events" },
];

function isNodeComplete(node: OpcUaNodeIdForm): boolean {
  if (node.identifierKind === "browsepath") return (node.hops ?? []).length > 0;
  return node.identifier.trim() !== "";
}

function describeNode(node: OpcUaNodeIdForm): string {
  if (node.identifierKind === "browsepath") {
    return `browsepath(${(node.hops ?? []).map((h) => h.targetName).join("/")})`;
  }
  return `ns=${node.namespaceIndex};${node.identifierKind}=${node.identifier}`;
}

/** Historical read configurations (OPC UA Part 11): which node, over what time range, at what resolution. */
export default function HistoryReadsEditor({
  historyReads,
  onChangeHistoryReads,
}: {
  historyReads: OpcUaHistoryReadForm[];
  onChangeHistoryReads: (rows: OpcUaHistoryReadForm[]) => void;
}) {
  const [t] = useTranslation("global");
  const [draft, setDraft] = useState<OpcUaHistoryReadForm>({
    node: { ...BLANK_NUMERIC_NODE_ID },
    kind: HistoryReadKind.Raw,
    startTime: "",
    endTime: "",
    numValuesPerNode: 0,
    returnBounds: false,
    aggregateTypeNode: null,
    processingIntervalMs: 0,
  });

  const canAdd = isNodeComplete(draft.node);
  const onAdd = () => {
    if (!canAdd) return;
    onChangeHistoryReads([...historyReads, draft]);
    setDraft({
      node: { ...BLANK_NUMERIC_NODE_ID },
      kind: HistoryReadKind.Raw,
      startTime: "",
      endTime: "",
      numValuesPerNode: 0,
      returnBounds: false,
      aggregateTypeNode: null,
      processingIntervalMs: 0,
    });
  };
  const onRemove = (index: number) =>
    onChangeHistoryReads(historyReads.filter((_, i) => i !== index));

  return (
    <div className="container mt-3" style={{ width: "100%", maxWidth: "none" }}>
      <Collapse>
        <Panel
          header={t("general.opcUaHistoryReads", { total: historyReads.length })}
          key="history-reads"
        >
          {historyReads.map((read, index) => (
            <div
              key={`${read.node.namespaceIndex}-${read.node.identifier}-${index}`}
              className="mb-3 generalStyles__flex"
              style={{ backgroundColor: "#1f1f1f", borderRadius: "5px", padding: "10px" }}
            >
              <div style={{ width: "100%" }}>
                <div className="generalStyles__textEllipsis">
                  {describeNode(read.node)} —{" "}
                  {HISTORY_READ_KIND_OPTIONS.find((o) => o.value === read.kind)?.label} [
                  {read.startTime || "…"} → {read.endTime || "…"}]
                </div>
              </div>
              <div className="generalStyles__flexCenter" style={{ width: "50px" }}>
                <Popconfirm
                  title="Delete this history read? This cannot be undone."
                  onConfirm={() => onRemove(index)}
                  okText="Yes, Delete"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                >
                  <Tooltip title="Delete">
                    <Button
                      aria-label="Delete"
                      type="primary"
                      danger
                      shape="circle"
                      icon={<FontAwesomeIcon icon={faTrash} />}
                    />
                  </Tooltip>
                </Popconfirm>
              </div>
            </div>
          ))}
          <NodeIdEditor node={draft.node} onChangeNode={(node) => setDraft({ ...draft, node })} />
          <div className="row mt-2">
            <div className="col-12 col-md-4 mt-2">
              <OpcUaFieldLabel label="Read Kind" tooltip={t("general.opcUaHistoryKindHelp")} />
              <Select
                className="generalStyles__width100"
                style={{ width: "100%", marginTop: 8 }}
                size="large"
                value={draft.kind}
                onChange={(kind: HistoryReadKind) => setDraft({ ...draft, kind })}
              >
                {HISTORY_READ_KIND_OPTIONS.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="col-12 col-md-4 mt-2">
              <OpcUaTextField
                label="Start Time (ISO 8601)"
                value={draft.startTime}
                onChange={(value) => setDraft({ ...draft, startTime: value })}
                tooltip={t("general.opcUaHistoryStartHelp")}
                placeholder="2026-01-31T08:00:00Z"
              />
            </div>
            <div className="col-12 col-md-4 mt-2">
              <OpcUaTextField
                label="End Time (ISO 8601)"
                value={draft.endTime}
                onChange={(value) => setDraft({ ...draft, endTime: value })}
                tooltip={t("general.opcUaHistoryEndHelp")}
                placeholder="2026-01-31T18:00:00Z"
              />
            </div>
          </div>
          <div className="row mt-2">
            <div className="col-12 col-md-4 mt-2">
              <OpcUaFieldLabel
                label="Num Values Per Node"
                tooltip={t("general.opcUaHistoryNumValuesHelp")}
              />
              <InputNumber
                className="generalStyles__width100"
                style={{ width: "100%", marginTop: 8 }}
                size="large"
                min={0}
                value={draft.numValuesPerNode}
                onChange={(v) => setDraft({ ...draft, numValuesPerNode: v ?? 0 })}
              />
            </div>
            <div className="col-12 col-md-4 mt-2">
              <OpcUaFieldLabel
                label="Processing Interval (ms, Processed kind)"
                tooltip={t("general.opcUaHistoryIntervalHelp")}
              />
              <InputNumber
                className="generalStyles__width100"
                style={{ width: "100%", marginTop: 8 }}
                size="large"
                min={0}
                value={draft.processingIntervalMs}
                onChange={(v) => setDraft({ ...draft, processingIntervalMs: v ?? 0 })}
              />
            </div>
            <div className="col-12 col-md-4 mt-2" style={{ textAlign: "center" }}>
              <OpcUaFieldLabel
                label="Return Bounds"
                tooltip={t("general.opcUaHistoryBoundsHelp")}
              />
              <div style={{ marginTop: 8, display: "flex", justifyContent: "center" }}>
                <Switch
                  checked={draft.returnBounds}
                  onChange={(checked) => setDraft({ ...draft, returnBounds: checked })}
                />
              </div>
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-12">
              <Button
                onClick={onAdd}
                disabled={!canAdd}
                className={`generalStyles__width100 ${canAdd ? "buttonStyle__28" : ""}`}
              >
                <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                Add
              </Button>
            </div>
          </div>
        </Panel>
      </Collapse>
    </div>
  );
}
